import { Link } from 'react-router-dom';
import { usePropertyProfile } from '@/hooks/usePropertyProfile';
import { googleMapsDirectionsUrl } from '@/lib/maps';
import { formatPhoneNumber, phoneDigits } from '@/lib/phone';

export default function Footer() {
  const property = usePropertyProfile();
  const address = property
    ? [property.addressLine1, property.addressLine2, property.city, property.state, property.country].filter(Boolean)
    : [];
  return (
    <footer className="bg-babcock-navy-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            {property?.logoUrl && <img src={property.logoUrl} alt={property.name} className="h-14 md:h-16 w-auto object-contain mb-4" title={property.name} />}
            <p className="text-gray-400 mt-4 text-sm leading-relaxed break-words">{property?.tagline}</p>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li><Link to="/search" className="text-gray-400 hover:text-babcock-navy-300 cursor-pointer">Book Now</Link></li>
              <li><Link to="/manage-booking" className="text-gray-400 hover:text-babcock-navy-300 cursor-pointer">Manage Booking</Link></li>
              <li><Link to="/check-in" className="text-gray-400 hover:text-babcock-navy-300 cursor-pointer">Online Check-in</Link></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">Services</h4>
            <ul className="space-y-2">
              <li><span className="text-gray-400">Room Service</span></li>
              <li><span className="text-gray-400">Concierge</span></li>
              <li><span className="text-gray-400">Business Center</span></li>
              <li><span className="text-gray-400">Fitness Center</span></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">Contact</h4>
            <div className="space-y-2 text-gray-400">
              <a
                href={property?.latitude != null && property?.longitude != null
                  ? googleMapsDirectionsUrl(property.latitude, property.longitude)
                  : (property?.mapsUrl || '#')}
                target="_blank"
                rel="noopener noreferrer"
                className="block hover:text-babcock-navy-300 transition-colors cursor-pointer"
              >
                {address.map((line) => <p key={line}>{line}</p>)}
              </a>
              {property?.whatsappNumber && (
                <a
                  href={`https://wa.me/${phoneDigits(property.whatsappNumber)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 hover:text-babcock-navy-300 transition-colors cursor-pointer"
                >
                  <i className="ri-whatsapp-line"></i>
                  {formatPhoneNumber(property.whatsappNumber)}
                </a>
              )}
              {property?.phone && (
                <a
                  href={`tel:${phoneDigits(property.phone)}`}
                  className="flex items-center gap-2 hover:text-babcock-navy-300 transition-colors cursor-pointer"
                >
                  <i className="ri-phone-line"></i>
                  {formatPhoneNumber(property.phone)}
                </a>
              )}
              {property?.contactNumbers?.map((contact) => (
                <a
                  key={contact.number}
                  href={contact.whatsappEnabled ? `https://wa.me/${phoneDigits(contact.number)}` : `tel:${phoneDigits(contact.number)}`}
                  target={contact.whatsappEnabled ? '_blank' : undefined}
                  rel={contact.whatsappEnabled ? 'noopener noreferrer' : undefined}
                  className="flex items-center gap-2 hover:text-babcock-navy-300 transition-colors cursor-pointer"
                >
                  <i className={contact.whatsappEnabled ? 'ri-whatsapp-line' : 'ri-phone-line'}></i>
                  {formatPhoneNumber(contact.number)}
                </a>
              ))}
              <a 
                href={property ? `mailto:${property.email}` : '#'}
                className="block hover:text-babcock-navy-300 transition-colors cursor-pointer"
              >
                Email: {property?.email}
              </a>
            </div>
          </div>
        </div>
        
        <div className="border-t border-babcock-navy-700 pt-8 text-center">
          <p className="text-gray-400">
            © {new Date().getFullYear()} {property?.name}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
